// HoneyBook vs Workspace369 yearly cost (LIST #85), shown on /blog/honeybook-price-increase by
// components/content/HoneybookCostCalculator.vue. Tested in tests/tracking/honeybookCost.test.ts.
// - HoneyBook prices come only from data/competitor-price-sources.json (its `plans` map, and each
//   value must also be a listed price). A missing price is null and the page shows "See site".
// - Workspace369 prices and seats come only from data/pricing-features.json.
// - List prices only: no tax, add-ons or payment costs (never compare fees).

export const HONEYBOOK_TIERS = ['Starter', 'Essentials', 'Premium'] as const;
export type HoneybookTier = (typeof HONEYBOOK_TIERS)[number];
export type Billing = 'monthly' | 'yearly';

// People each tier holds, from data/competitors/honeybook.json (seats_users, checked 2026-10-24):
// Starter can't add team members; Essentials lets the owner add up to 2; Premium is unlimited.
// The owner is not counted in Essentials' 2, as the post's own table notes.
const HONEYBOOK_CAPACITY: Record<HoneybookTier, number> = { Starter: 1, Essentials: 3, Premium: Infinity };

// Workspace369 plan matched to each tier (data/pricing-features.json, data/competitors/honeybook.json):
// Starter is solo with no team members → Cadet (1 seat); Essentials and Premium add team members →
// Voyager (team seats). Email workflows are on every Workspace369 plan, so they don't decide the match. HoneyBook's scheduler and
// expense tracking match online booking and expenses, which start at Specialist.
const MATCHED_PLAN: Record<HoneybookTier, string> = { Starter: 'cadet', Essentials: 'voyager', Premium: 'voyager' };
const BOOKING_PLAN = 'specialist';

export interface HoneybookPrices {
  checkedOn: string;
  plans: Record<HoneybookTier, { monthly: number | null; yearly: number | null }>;
}

export interface WorkspacePlan {
  slug: string;
  name: string;
  monthly: number;
  yearlyTotal: number;
  seats: number;
  extraSeat: number | null;
}

const dollars = (value: unknown) => {
  const number = Number(String(value ?? '').replace(/[$,]/g, ''));
  return String(value ?? '').trim() && Number.isFinite(number) ? number : null;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

type PriceEntry = string | { price: string };
interface PriceVendor {
  vendor: string;
  checkedOn?: string;
  prices?: PriceEntry[];
  plans?: Record<string, { monthly?: string; yearly?: string }>;
}

export const honeybookPrices = (config: { vendors: PriceVendor[] }): HoneybookPrices => {
  const vendor = config.vendors.find((entry) => entry.vendor === 'HoneyBook');
  const listed = new Set((vendor?.prices || []).map((price) => (typeof price === 'string' ? price : price.price)));
  const listedPrice = (value: string | undefined) => (value && listed.has(value) ? dollars(value) : null);
  const plans = Object.fromEntries(
    HONEYBOOK_TIERS.map((tier) => [
      tier,
      { monthly: listedPrice(vendor?.plans?.[tier]?.monthly), yearly: listedPrice(vendor?.plans?.[tier]?.yearly) },
    ]),
  ) as HoneybookPrices['plans'];
  return { checkedOn: vendor?.checkedOn || '', plans };
};

interface PricingFeatures {
  plans: Array<{ slug: string; name: string; monthly: string; yearlyTotal: string; seats: number }>;
  categories: Array<{ rows: Array<{ key?: string; cells?: string[] }> }>;
}

export const workspacePlans = (pricing: PricingFeatures): WorkspacePlan[] => {
  const seatRow = pricing.categories.flatMap((category) => category.rows).find((row) => row.key === 'additionalSeats');
  return pricing.plans.map((plan, index) => ({
    slug: plan.slug,
    name: plan.name,
    monthly: dollars(plan.monthly) ?? 0,
    yearlyTotal: dollars(plan.yearlyTotal) ?? 0,
    seats: plan.seats,
    extraSeat: dollars(seatRow?.cells?.[index]),
  }));
};

export interface CostInput {
  honeybookPlan: HoneybookTier;
  billing: Billing;
  teamSize: number;
  bookingOrExpenses: boolean;
}

export interface CostResult {
  teamSize: number;
  billing: Billing;
  honeybook: { plan: HoneybookTier; movedUp: boolean; perMonth: number | null; yearly: number | null };
  workspace: { plan: WorkspacePlan; extraSeats: number; yearly: number };
  // HoneyBook minus Workspace369 per year: positive means Workspace369 costs less. null = "See site".
  difference: number | null;
}

// Extra seats are $10 per seat per month on both billings (the matrix tip prices them per month).
const workspaceYearly = (plan: WorkspacePlan, billing: Billing, extraSeats: number) =>
  round2((billing === 'yearly' ? plan.yearlyTotal : plan.monthly * 12) + extraSeats * (plan.extraSeat ?? 0) * 12);

export const compareCosts = (input: CostInput, honeybook: HoneybookPrices, plans: WorkspacePlan[]): CostResult => {
  const teamSize = Math.min(500, Math.max(1, Math.round(Number(input.teamSize) || 1)));

  const startTier = HONEYBOOK_TIERS.indexOf(input.honeybookPlan);
  const tier = HONEYBOOK_TIERS.slice(startTier).find((name) => HONEYBOOK_CAPACITY[name] >= teamSize) || 'Premium';
  const perMonth = honeybook.plans[tier][input.billing];

  // The cheapest plan from the matched one up that fits the team (Cadet has no extra seats).
  const minimumSlug = input.bookingOrExpenses ? BOOKING_PLAN : MATCHED_PLAN[tier];
  const minimumIndex = plans.findIndex((plan) => plan.slug === minimumSlug);
  if (minimumIndex < 0) throw new Error(`honeybookCost: plan "${minimumSlug}" is missing from pricing-features.json`);
  const options = plans
    .slice(minimumIndex)
    .filter((plan) => plan.seats >= teamSize || plan.extraSeat !== null)
    .map((plan) => {
      const extraSeats = Math.max(0, teamSize - plan.seats);
      return { plan, extraSeats, yearly: workspaceYearly(plan, input.billing, extraSeats) };
    });
  if (!options.length) throw new Error('honeybookCost: no Workspace369 plan fits this team');
  const workspace = options.reduce((best, option) => (option.yearly < best.yearly ? option : best), options[0]);

  const honeybookYearly = perMonth === null ? null : round2(perMonth * 12);
  return {
    teamSize,
    billing: input.billing,
    honeybook: { plan: tier, movedUp: tier !== input.honeybookPlan, perMonth, yearly: honeybookYearly },
    workspace,
    difference: honeybookYearly === null ? null : round2(honeybookYearly - workspace.yearly),
  };
};
